import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Space, Statistic, Typography } from 'antd';
import {
  CalendarOutlined,
  CarOutlined,
  TeamOutlined,
  UserOutlined,
  PhoneOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  FundOutlined,
} from '@ant-design/icons';
import { AInput, ASelect, ASwitch, AButton } from '../../../components';
import { useToast } from '../../../hooks/toast';
import { ICovenant, IProfessional } from '../../../shared/interfaces';
import { getSelectCovenants, getSelectProfessionals } from '../../../http';
import {
  calculateCrmAudience,
  CrmCampaignCategory,
  IResponseAudience,
} from '../../../http/crm';

const { Text } = Typography;

export interface AudienceFilters {
  birthday_month?: number | null;
  inactive_days_min?: number | null;
  inactive_days_max?: number | null;
  covenant?: string | null;
  professional_ids?: string[] | null;
  future_schedule?: boolean | null;
  age_min?: number | null;
  age_max?: number | null;
  gender?: string | null;
  has_phone_only?: boolean | null;
  status_client?: number | null;
}

interface AudienceBuilderProps {
  value?: AudienceFilters | null;
  onChange?: (filters: AudienceFilters | null, audience: IResponseAudience | null) => void;
  category?: CrmCampaignCategory;
  compact?: boolean;
}

const MONTH_OPTIONS = [
  { label: 'Janeiro', value: 1 },
  { label: 'Fevereiro', value: 2 },
  { label: 'Março', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Maio', value: 5 },
  { label: 'Junho', value: 6 },
  { label: 'Julho', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Setembro', value: 9 },
  { label: 'Outubro', value: 10 },
  { label: 'Novembro', value: 11 },
  { label: 'Dezembro', value: 12 },
];

const GENDER_OPTIONS = [
  { label: 'Masculino', value: 'MALE' },
  { label: 'Feminino', value: 'FEMALE' },
  { label: 'Outro / Não informado', value: 'OTHER' },
];

const STATUS_CLIENT_OPTIONS = [
  { label: 'Ativo', value: 1 },
  { label: 'Inativo', value: 0 },
];

const AudienceBuilder: React.FC<AudienceBuilderProps> = ({
  value,
  onChange,
  category,
  compact = false,
}) => {
  const { addToast } = useToast();

  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(value?.birthday_month ?? null);
  const [inactiveDaysMin, setInactiveDaysMin] = useState<number | null>(value?.inactive_days_min ?? null);
  const [inactiveDaysMax, setInactiveDaysMax] = useState<number | null>(value?.inactive_days_max ?? null);
  const [covenant, setCovenant] = useState<string | null>(value?.covenant ?? null);
  const [professionalIds, setProfessionalIds] = useState<string[]>(value?.professional_ids ?? []);
  const [futureSchedule, setFutureSchedule] = useState<boolean>(value?.future_schedule ?? false);
  const [ageMin, setAgeMin] = useState<number | null>(value?.age_min ?? null);
  const [ageMax, setAgeMax] = useState<number | null>(value?.age_max ?? null);
  const [gender, setGender] = useState<string | null>(value?.gender ?? null);
  const [hasPhoneOnly, setHasPhoneOnly] = useState<boolean>(value?.has_phone_only ?? false);
  const [statusClient, setStatusClient] = useState<number | null>(value?.status_client ?? null);

  const [covenants, setCovenants] = useState<ICovenant[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [audienceResult, setAudienceResult] = useState<IResponseAudience | null>(null);
  const [loadingAudience, setLoadingAudience] = useState<boolean>(false);

  const loadCovenants = useCallback(async () => {
    try {
      const data = await getSelectCovenants();
      setCovenants(data || []);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar convênios.',
      });
    }
  }, [addToast]);

  const loadProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data || []);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar profissionais.',
      });
    }
  }, [addToast]);

  useEffect(() => {
    loadCovenants();
    loadProfessionals();
  }, [loadCovenants, loadProfessionals]);

  const buildFilters = useCallback((): AudienceFilters => {
    return {
      birthday_month: birthdayMonth,
      inactive_days_min: inactiveDaysMin,
      inactive_days_max: inactiveDaysMax,
      covenant: covenant,
      professional_ids: professionalIds && professionalIds.length > 0 ? professionalIds : null,
      future_schedule: futureSchedule || null,
      age_min: ageMin,
      age_max: ageMax,
      gender: gender,
      has_phone_only: hasPhoneOnly || null,
      status_client: statusClient,
    };
  }, [
    birthdayMonth,
    inactiveDaysMin,
    inactiveDaysMax,
    covenant,
    professionalIds,
    futureSchedule,
    ageMin,
    ageMax,
    gender,
    hasPhoneOnly,
    statusClient,
  ]);

  const handleCalculateAudience = useCallback(async () => {
    setLoadingAudience(true);
    try {
      const filters = buildFilters();
      const cleaned: Record<string, unknown> = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
          if (Array.isArray(v)) {
            if (v.length > 0) cleaned[k] = v;
          } else {
            cleaned[k] = v;
          }
        }
      });
      const response = await calculateCrmAudience(cleaned, category);
      setAudienceResult(response);
      if (onChange) {
        onChange(filters, response);
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao calcular audiência.',
      });
    } finally {
      setLoadingAudience(false);
    }
  }, [buildFilters, category, onChange, addToast]);

  const handleClearFilters = useCallback(() => {
    setBirthdayMonth(null);
    setInactiveDaysMin(null);
    setInactiveDaysMax(null);
    setCovenant(null);
    setProfessionalIds([]);
    setFutureSchedule(false);
    setAgeMin(null);
    setAgeMax(null);
    setGender(null);
    setHasPhoneOnly(false);
    setStatusClient(null);
    setAudienceResult(null);
    if (onChange) {
      onChange(null, null);
    }
  }, [onChange]);

  useEffect(() => {
    if (value) {
      setBirthdayMonth(value.birthday_month ?? null);
      setInactiveDaysMin(value.inactive_days_min ?? null);
      setInactiveDaysMax(value.inactive_days_max ?? null);
      setCovenant(value.covenant ?? null);
      setProfessionalIds(value.professional_ids ?? []);
      setFutureSchedule(value.future_schedule ?? false);
      setAgeMin(value.age_min ?? null);
      setAgeMax(value.age_max ?? null);
      setGender(value.gender ?? null);
      setHasPhoneOnly(value.has_phone_only ?? false);
      setStatusClient(value.status_client ?? null);
    }
  }, [value]);

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <TeamOutlined style={{ color: '#1890ff' }} />
          <span>Construtor de Audiência</span>
        </Space>
      }
      extra={
        <Space>
          <AButton onClick={handleClearFilters} size="small">
            Limpar
          </AButton>
          <AButton
            type="primary"
            size="small"
            icon={<FundOutlined />}
            loading={loadingAudience}
            onClick={handleCalculateAudience}
          >
            Calcular Audiência
          </AButton>
        </Space>
      }
      style={{ marginBottom: compact ? 0 : 16 }}
    >
      <Form layout="vertical" style={{ marginTop: 8 }}>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <CalendarOutlined /> <span>Mês de aniversário</span>
                </Space>
              }
            >
              <ASelect
                placeholder="Selecione o mês"
                allowClear
                value={birthdayMonth}
                onChange={(v) => setBirthdayMonth(v ?? null)}
                options={MONTH_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Space size={6}>
                  <UserOutlined /> <span>Gênero</span>
                </Space>
              }
            >
              <ASelect
                placeholder="Selecione o gênero"
                allowClear
                value={gender}
                onChange={(v) => setGender(v ?? null)}
                options={GENDER_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <IdcardOutlined /> <span>Idade mínima</span>
                </Space>
              }
            >
              <AInput
                type="number"
                min={0}
                max={150}
                placeholder="Ex: 18"
                value={ageMin ?? ''}
                onChange={(e) =>
                  setAgeMin(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <IdcardOutlined /> <span>Idade máxima</span>
                </Space>
              }
            >
              <AInput
                type="number"
                min={0}
                max={150}
                placeholder="Ex: 65"
                value={ageMax ?? ''}
                onChange={(e) =>
                  setAgeMax(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <SafetyCertificateOutlined /> <span>Status cliente</span>
                </Space>
              }
            >
              <ASelect
                placeholder="Selecione o status"
                allowClear
                value={statusClient}
                onChange={(v) => setStatusClient(v ?? null)}
                options={STATUS_CLIENT_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <CarOutlined /> <span>Convênio</span>
                </Space>
              }
            >
              <ASelect
                showSearch
                placeholder="Selecione o convênio"
                allowClear
                value={covenant}
                onChange={(v) => setCovenant(v ?? null)}
                filterOption={(input, option: any) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={covenants.map((c) => ({
                  label: c.description,
                  value: c.id,
                }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={16}>
            <Form.Item
              label={
                <Space size={6}>
                  <UserOutlined /> <span>Profissionais responsáveis</span>
                </Space>
              }
            >
              <ASelect
                mode="multiple"
                showSearch
                placeholder="Selecione um ou mais profissionais"
                allowClear
                value={professionalIds}
                onChange={(v) => setProfessionalIds(v as string[])}
                filterOption={(input, option: any) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={professionals.map((p) => ({
                  label: p.name,
                  value: p.id,
                }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <CarOutlined /> <span>Dias inativos (mín.)</span>
                </Space>
              }
            >
              <AInput
                type="number"
                min={0}
                placeholder="Ex: 90"
                value={inactiveDaysMin ?? ''}
                onChange={(e) =>
                  setInactiveDaysMin(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={6}>
                  <CarOutlined /> <span>Dias inativos (máx.)</span>
                </Space>
              }
            >
              <AInput
                type="number"
                min={0}
                placeholder="Ex: 365"
                value={inactiveDaysMax ?? ''}
                onChange={(e) =>
                  setInactiveDaysMax(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label="Agendamento futuro"
              style={{ marginBottom: 0 }}
            >
              <Space align="center" size={10}>
                <ASwitch
                  checked={futureSchedule}
                  onChange={() => setFutureSchedule(!futureSchedule)}
                />
                <Text type="secondary">
                  {futureSchedule ? 'Apenas com agendamento futuro' : 'Ignorar agenda futura'}
                </Text>
              </Space>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label="Apenas com telefone"
              style={{ marginBottom: 0 }}
            >
              <Space align="center" size={10}>
                <ASwitch
                  checked={hasPhoneOnly}
                  onChange={() => setHasPhoneOnly(!hasPhoneOnly)}
                />
                <Text type="secondary">
                  {hasPhoneOnly ? 'Apenas clientes com WhatsApp' : 'Todos clientes'}
                </Text>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        {audienceResult && (
          <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: '#f0f9ff' }}>
                <Statistic
                  title="Total de clientes (estimado)"
                  value={audienceResult.count}
                  prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: '#f6ffed' }}>
                <Statistic
                  title="Amostra (primeiros)"
                  value={audienceResult.clients?.length ?? 0}
                  prefix={<PhoneOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: '#fffbe6' }}>
                <Statistic
                  title="Filtros aplicados"
                  value={Object.keys(audienceResult.filters_applied || {}).length}
                  prefix={<SafetyCertificateOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Form>
    </Card>
  );
};

export default AudienceBuilder;
